/**
 * Fixed path for the orchestration API (everything before `/sessions`). Every
 * deployment proxies this same path to whatever backend it targets (Vite dev
 * proxy locally, nginx/CloudFront in deployed environments) — the bundle never
 * bakes in a backend hostname or environment id, so one build works against
 * any backend the deploy-time proxy points it at.
 */
const ORCHESTRATION_BASE = '/api/orchestration';

export function orchestrationUrl(path: string): string {
  return `${ORCHESTRATION_BASE}${path}`;
}

const DEFAULT_ENV_ID = 'local';

/**
 * `VITE_ENV` is only consumed for the pieces that genuinely can't be resolved
 * by proxying a path — the Keycloak client id (a value Keycloak itself
 * validates, not a URL) and debug/dev-mode guards. It has no bearing on API
 * routing; see `orchestrationUrl` above.
 */
export function getActiveEnvId(): string {
  return import.meta.env.VITE_ENV?.trim() || DEFAULT_ENV_ID;
}

const KEYCLOAK_CLIENT_IDS: Record<string, string> = {
  dev: 'archer-ai-dev',
  qa: 'archer-ai-qa',
  uat: 'archer-ai-uat',
  prod: 'archer-ai-prod',
};

/** Default Keycloak client id for the active environment (developer mode). */
export function getKeycloakClientId(): string {
  return import.meta.env.VITE_DEV_CLIENT_ID?.trim() || KEYCLOAK_CLIENT_IDS[getActiveEnvId()] || '';
}

/**
 * Keycloak token endpoint used by developer-mode sign-in. Defaults to the
 * proxied path (`/api/keycloak/...`) so the browser avoids CORS — Vite forwards
 * it to the real Keycloak host (see vite.config.ts).
 */
export function getKeycloakTokenUrl(): string {
  return (
    import.meta.env.VITE_KEYCLOAK_TOKEN_URL?.trim() ||
    '/api/keycloak/realms/archer/protocol/openid-connect/token'
  );
}

/**
 * Debug-mode toggle for developer-facing chat affordances (e.g. the "Tools
 * used" footer). Off unless explicitly enabled — never on by default, even
 * in dev builds, since it's meant to be opted into per session, not always-on.
 */
export function isDebugMode(): boolean {
  return import.meta.env.VITE_DEBUG_MODE?.trim().toLowerCase() === 'true';
}
